import { useState } from 'react';
import Dialog from '@material-ui/core/Dialog';
import AddCommiteeData from '../../../components/addUser/AddCommittee.component';
import SuccessConfirmation from '../../../components/addUser/SuccessConfirmation.component';
import { createNewUser } from '../../../services/users/users.service';
import { UserDto } from '../../../models/userDto.types';
import { UserM, AccountTypeEnum } from '../../../models/user.types';
import AddPersonalData from '../../../components/addUser/AddPersonalData.component';
import { UserPermission } from '../../../enum/userPermissions';
import { AxiosResponse } from 'axios';
import { ButtonOne } from '../../../components/ButtonOne/ButtonOne.component';
import { useStyles } from './ExaminerCreate.styles';
import useSessionUser from '../../../hooks/useSessionUser';
import { hasOnePermissionToAccess } from '../../../services/users/identity/user.service';
import { useTranslation } from 'react-i18next';
import { useLayoutContext } from '../../../Context/Layout.context';

interface AddExaminerProps {
  userUpdated: () => void;
}

export default function ExaminerCreate(props: AddExaminerProps) {
  const { user } = useSessionUser();
  const { t } = useTranslation();
  const { openAlert } = useLayoutContext();
  const [open, setOpen] = useState<boolean>(false);

  const [name, setName] = useState<string>('');
  const [surname, setSurname] = useState<string>('');
  const [phone, setPhone] = useState<string>('');
  const [phoneHidden, setPhoneHidden] = useState<boolean>(true);
  const [pesel, setPesel] = useState<string>('');
  const [mail, setMail] = useState<string>('');
  const [commitee, setCommitee] = useState<string>('');
  const [accountType, setAccountType] = useState<AccountTypeEnum | null>(null);
  const [expDate, setExpDate] = useState<Date | null>(null);

  const [stage1, setStage1] = useState<boolean>(false);
  const [stage2, setStage2] = useState<boolean>(false);
  const [stage3, setStage3] = useState<boolean>(false);

  const [close1] = useState<boolean>(false);
  const [close2] = useState<boolean>(false);
  const [close3] = useState<boolean>(false);

  const [isSuccess, setIsSuccess] = useState<boolean>(false);
  const [temporaryScreen] = useState<boolean>(false);
  const [isSubmit, setIsSubmit] = useState<boolean>(false);

  const classes = useStyles();

  const handleClickOpen = () => {
    setOpen(true);
    setName('');
    setSurname('');
    setPesel('');
    setMail('');
    setPhone('');
    setPhoneHidden(true);
    setCommitee('');
    setAccountType(null);
    setExpDate(new Date());
  };

  const saveUser = (_commitee: number, _expDate?: Date | null) => {
    const dto: UserDto = {
      firstName: name,
      lastName: surname,
      pesel: pesel,
      phone: phone,
      phoneHidden: phoneHidden,
      email: mail,
      accountTypeId: AccountTypeEnum.Examiner,
      expirationDate: _expDate,
      examCommitteeId: _commitee,
    };
    createNewUser(dto).then(
      (result: AxiosResponse<UserM>) => {
        result.status == 201 ? setIsSuccess(true) : setIsSuccess(false);
        setIsSubmit(true);
        props.userUpdated();
      },
      () => {
        if (!navigator.onLine) {
          openAlert('error', 'W wyniku braku połączenia z internetem zapisywanie danych nie powiodło się.');
        } else openAlert('save_error');
        setOpen(false);
      },
    );
  };

  const personalDataToParent = (
    _name: string,
    _surname: string,
    _pesel: string,
    _phone: string,
    _phoneHidden: boolean,
    _mail: string,
    _close: boolean,
    _toStage2: boolean,
  ) => {
    setName(_name);
    setSurname(_surname);
    setPhone(_phone);
    setPhoneHidden(_phoneHidden);
    setPesel(_pesel);
    setMail(_mail);
    setOpen(!_close);

    if (_close) {
      setStage1(false);
      setStage2(false);
      setStage3(false);
    } else {
      setStage1(!_toStage2);
      setStage2(_toStage2);
      setStage3(!_toStage2);
    }
  };

  const commiteeDataToParent = (
    _commitee: string,
    _accType: AccountTypeEnum | null,
    _expDate: Date | null,
    _toStage3: boolean,
    _close: boolean,
    _previous: boolean,
  ) => {
    setCommitee(_commitee);
    setAccountType(_accType);
    setExpDate(_expDate);

    setOpen(!_close);

    if (_close) {
      setStage1(false);
      setStage2(false);
      setStage3(false);
      saveUser(parseInt(_commitee), _expDate);
    } else if (_previous) {
      setStage2(false);
      setStage1(true);
    } else {
      setStage1(!_toStage3);
      setStage3(_toStage3);
      setStage2(!_toStage3);
      saveUser(parseInt(_commitee), _expDate);
    }
  };

  const successDataToParent = (_close: boolean) => {
    setOpen(!_close);
    setStage1(!_close);
    setStage2(!_close);
    setStage3(!_close);
  };

  const permission = hasOnePermissionToAccess(user, [
    UserPermission.AdminExaminerCreate,
    UserPermission.AdminOkeExaminerCreate,
  ]);

  return (
    <div>
      <ButtonOne
        className={classes.addUserButton}
        variant="contained"
        color="primary"
        disabled={!permission}
        onClick={() => {
          setStage1(true);
          setStage2(false);
          setStage3(false);
          handleClickOpen();
        }}
      >
        {permission ? <span style={{ margin: '0px 10px 0px -4px', fontSize: '20px' }}>+</span> : ''}{' '}
        {t('examiners:header.addExaminerButton')}
      </ButtonOne>
      <Dialog
        onClose={(event, reason) => {
          if (reason !== 'backdropClick') {
            setOpen(false);
          }
        }}
        aria-label="dodaj egzaminatora"
        open={open}
      >
        {stage1 && (
          <AddPersonalData
            nameArg={name}
            surnameArg={surname}
            peselArg={pesel}
            phoneArg={phone}
            phoneHiddenArg={phoneHidden}
            mailArg={mail}
            closeArg={close1}
            toStage2={stage2}
            personalDataToParent={personalDataToParent}
          />
        )}
        {stage2 && accountType !== undefined && (
          <AddCommiteeData
            isExaminerReadOnly={true}
            commiteeArg={commitee}
            accTypeArg={accountType}
            expDateArg={expDate}
            closeArgm={close2}
            toStage3={stage3}
            commiteeDataToParent={commiteeDataToParent}
          />
        )}
        {stage3 && isSubmit && (
          <SuccessConfirmation
            temporaryArg={temporaryScreen}
            isSuccessArg={isSuccess}
            closeArg={close3}
            mail={mail}
            successDataToParent={successDataToParent}
          />
        )}
      </Dialog>
    </div>
  );
}
